/* WEEK EDITOR STAGE 1 IN THE EXAMPLE (2026-10-19). William judges the web in the example workspace, so the editor must WORK
 * there: `write_week_trees` is answered by applying it to THIS TAB's made-up world — in memory, gone on reload. Nothing is
 * sent: `mode.ts` decides that before this is consulted, and every other write is still refused. It mirrors the SQL
 * function's rules that a coach can SEE (the token, a trained day, a library template, rows of a workout outside the call,
 * dropping only an empty copy); it is not the permission proof — that is the rehearsal against the live database. */
import type { Fixture, Rows } from "./rest";
import type { TreePayload } from "../weekEdit";

type Row = Record<string, unknown>;

function fail(word: string, detail: string): { error: string } {
  return { error: `WEEK-${word}: ${detail}` };
}

export function applyWeekTrees(fx: Fixture, payloads: readonly TreePayload[], now: () => string): { updated: Record<string, string> } | { error: string } {
  const t = fx.tables;
  const table = (name: string): Rows => (t[name] ??= []);
  const workouts = table("coach_workouts");
  const assignments = table("coach_assignments");
  const sections = table("coach_workout_sections");
  const exercises = table("coach_workout_exercises");
  const sets = table("coach_workout_sets");
  const ids = new Set(payloads.map((p) => p.workout_id));

  /* Checks first, nothing changed until every one passes (the SQL is one transaction). */
  const clients = new Set<unknown>();
  for (const p of payloads) {
    const w = workouts.find((x) => x["id"] === p.workout_id);
    if (p.create !== undefined) {
      if (w !== undefined) return fail("FOREIGN", "that workout already exists");
      clients.add(p.create.client_id);
      continue;
    }
    if (w === undefined) return fail("STALE", "the workout is gone");
    if (w["client_id"] === null || w["client_id"] === undefined) return fail("TEMPLATE", "a library workout");
    clients.add(w["client_id"]);
    if ((p.expected_updated_at ?? null) !== (w["updated_at"] ?? null)) return fail("STALE", "changed since it was read");
    if (assignments.some((a) => a["workout_id"] === p.workout_id && a["status"] === "completed")) return fail("TRAINED", "a trained day");
  }
  /* A DROP NEVER DELETES A ROW: every exercise row the dropped copy holds must land in another workout of the same call (an
     Undo of "moved to a new workout on Thu" sends the rows home AND drops the copy, in one call). */
  const landing = new Set(payloads.flatMap((p) => (p.sections ?? []).flatMap((s) => s.exercises.map((e) => e.id))));
  for (const p of payloads) {
    if (p.drop !== true) continue;
    const own = new Set(sections.filter((s) => s["workout_id"] === p.workout_id).map((s) => s["id"]));
    if (exercises.some((e) => own.has(e["section_id"]) && !landing.has(String(e["id"])))) return fail("DROP", "not empty");
  }
  if (clients.size > 1) return fail("CLIENTS", "two clients in one call");
  const sectionOwner = new Map(sections.map((s) => [s["id"], s["workout_id"]]));
  const exerciseOwner = new Map(exercises.map((e) => [e["id"], sectionOwner.get(e["section_id"])]));
  for (const p of payloads) for (const s of p.sections ?? []) {
    const o = sectionOwner.get(s.id);
    if (o !== undefined && !ids.has(String(o))) return fail("FOREIGN", "a section of another workout");
    for (const e of s.exercises) {
      const eo = exerciseOwner.get(e.id);
      if (eo !== undefined && !ids.has(String(eo))) return fail("FOREIGN", "a row of another workout");
      for (const x of e.sets) {
        const so = sets.find((r) => r["id"] === x.id);
        const own = so === undefined ? undefined : exerciseOwner.get(so["workout_exercise_id"]);
        if (so !== undefined && !ids.has(String(own))) return fail("FOREIGN", "a set of another workout");
      }
    }
  }

  /* Apply: every touched workout's tree is replaced by the call's, KEEPING each row's other columns (legacy ones included). */
  const before = { sections: new Map(sections.map((r) => [r["id"], r])), exercises: new Map(exercises.map((r) => [r["id"], r])), sets: new Map(sets.map((r) => [r["id"], r])) };
  const touchedSections = new Set(sections.filter((s) => ids.has(String(s["workout_id"]))).map((s) => s["id"]));
  const touchedExercises = new Set(exercises.filter((e) => touchedSections.has(e["section_id"])).map((e) => e["id"]));
  t.coach_workout_sets = sets.filter((x) => !touchedExercises.has(x["workout_exercise_id"]));
  t.coach_workout_exercises = exercises.filter((e) => !touchedSections.has(e["section_id"]));
  t.coach_workout_sections = sections.filter((s) => !ids.has(String(s["workout_id"])));
  const stamp = now();
  const updated: Record<string, string> = {};
  for (const p of payloads) {
    if (p.create !== undefined) {
      const day = p.create.scheduled_date;
      const onDay = assignments.filter((a) => a["client_id"] === p.create!.client_id && a["scheduled_date"] === day).length;
      workouts.push({
        id: p.workout_id, coach_id: fx.coachId, client_id: p.create.client_id, root_workout_id: null, kind: "workout", name: p.create.name,
        description: null, archived_at: null, check_in_fields: null, task_prompt: null, task_optional: false, created_at: stamp, updated_at: stamp,
      });
      assignments.push({
        id: p.create.assignment_id, coach_id: fx.coachId, client_id: p.create.client_id, workout_id: p.workout_id, root_workout_id: null,
        scheduled_date: day, status: "assigned", hidden_from_client: false, is_deload: false, note: "", assigned_at: stamp,
        completed_at: null, completed_minutes: [], completed_snapshot: null, day_position: onDay,
      });
    }
    if (p.drop === true) {
      t.coach_workouts = (t.coach_workouts ?? []).filter((w) => w["id"] !== p.workout_id);
      t.coach_assignments = (t.coach_assignments ?? []).filter((a) => a["workout_id"] !== p.workout_id);
      continue;
    }
    for (const s of p.sections ?? []) {
      const { exercises: rows, ...sec } = s;
      t.coach_workout_sections!.push({ format_config: null, reminder_times: [], ...(before.sections.get(s.id) as Row | undefined), ...sec, workout_id: p.workout_id });
      for (const e of rows) {
        const { sets: list, ...ex } = e;
        t.coach_workout_exercises!.push({
          sets: null, reps_min: null, reps_max: null, hold_seconds_min: null, hold_seconds_max: null, rest_seconds: null,
          ...(before.exercises.get(e.id) as Row | undefined), ...ex, section_id: s.id,
        });
        for (const x of list) t.coach_workout_sets!.push({ ...(before.sets.get(x.id) as Row | undefined), ...x, workout_exercise_id: e.id });
      }
    }
    const w = (t.coach_workouts ?? []).find((x) => x["id"] === p.workout_id);
    if (w !== undefined) { w["updated_at"] = stamp; updated[p.workout_id] = stamp; }
  }
  return { updated };
}
